import React, { useState } from 'react';
import s from '../../styles/AdminDocumentMap.module.css';

export interface FieldTypeRow {
  id: string;
  name: string;
  value_type: string;
  required: boolean;
  viewingRole: string;
  edittingRole: string;
}

interface FieldTypeTableProps {
  rows: FieldTypeRow[];
  onSave: (row: FieldTypeRow) => Promise<void>;
  onDelete: (id: string) => void;
}

const FieldTypeTable: React.FC<FieldTypeTableProps> = ({ rows, onSave, onDelete }) => {
  const [editingRow, setEditingRow] = useState<FieldTypeRow | null>(null);
  
  const handleSave = async () => {
    if (!editingRow) return;
    const rowToSave = editingRow;
    setEditingRow(null);
    await onSave(rowToSave);
  };

  const getTypeText = (type: string) => {
    switch(type) {
      case "string":
        return "Text";
      case "boolean": 
        return "Boolean";
      case "date":
        return "Date";
      default:
        return "Select Type";
    }
  }

  const getRoleText = (role: string) => {
    switch(role) {
      case "agent": return "Agent";
      case "manager": return "Manager";
      case "admin": return "Admin";
      default: return role || '—';
    }
  }

  if (rows == null || rows.length === 0) return <p className={s.emptyState}>None yet.</p>;
  return (
    <table className={s.table}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Required</th>
          <th>Viewing Role</th>
          <th>Editing Role</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            {editingRow?.id === row.id ? (
              <>
                <td>
                  <input
                    className={s.inlineInput}
                    value={editingRow.name}
                    onChange={e => setEditingRow({ ...editingRow, name: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditingRow(null); }}
                    autoFocus
                  />
                </td>
                <td>
                  <select
                    className={s.inlineInput}
                    value={editingRow.value_type}
                    onChange={e => setEditingRow({ ...editingRow, value_type: e.target.value })}
                  >
                    <option value=""> Select Type </option>
                    <option value="boolean">Boolean</option>
                    <option value="string">Text</option>
                    <option value="date">Date</option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={editingRow.required}
                    onChange={e => setEditingRow({ ...editingRow, required: e.target.checked })}
                  />
                </td>
                <td>
                  <select
                    className={s.inlineInput}
                    value={editingRow.viewingRole}
                    onChange={e => setEditingRow({ ...editingRow, viewingRole: e.target.value })}
                  >
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <select
                    className={s.inlineInput}
                    value={editingRow.edittingRole}
                    onChange={e => setEditingRow({ ...editingRow, edittingRole: e.target.value })}
                  >
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <button className={`${s.btnSm} ${s.btnPrimary}`} onClick={handleSave}>Save</button>
                  <button className={`${s.btnSm} ${s.btnSecondary}`} onClick={() => setEditingRow(null)}>Cancel</button>
                </td>
              </>
            ) : (
              <>
                <td>{row.name}</td>
                <td>{getTypeText(row.value_type)}</td>
                <td>{row.required ? 'Yes' : 'No'}</td>
                <td>{getRoleText(row.viewingRole)}</td>
                <td>{getRoleText(row.edittingRole)}</td>
                <td>
                  <button className={s.iconBtn} title="Edit" onClick={() => setEditingRow({ ...row })}>✏️</button>
                  <button className={`${s.iconBtn} ${s.iconBtnDanger}`} title="Delete" onClick={() => onDelete(row.id)}>🗑️</button>
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default FieldTypeTable;
